import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { calendarAPI, visitTypeAPI } from "../../../services/calendarService";
import Table from "../../../components/Common/Table/Table";
import ActionButtons from "../../../components/Common/ActionButtons/ActionButtons";
import StatusBadge from "../../../components/Common/StatusBadge/StatusBadge";
import ListScreenSearchBar from "../../../components/Common/ListScreenSearchBar/ListScreenSearchBar";
import ListScreenPaginationBar from "../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar";
import ConfirmDeleteModal from "../../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal";
import { useBreadcrumb } from "../../../contexts/BreadcrumbContext";
import "../../../components/Common/ListScreenPageLayout/listScreenPage.css";
import "./VisitType.css";
import { matchesWordStartAny } from "../../../utils/searchMatch";

const VisitType = () => {
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [visitTypes, setVisitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVisitType, setEditingVisitType] = useState(null);
  const [practices, setPractices] = useState([]);

  // Pagination and Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    practice_id: "",
    title: "",
    description: "",
    duration: "",
    status: "active",
    color: "#FF5733"
  });
  const [errors, setErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visitTypeToDelete, setVisitTypeToDelete] = useState(null);
  const [isDeletingVisitType, setIsDeletingVisitType] = useState(false);
  const [permissions, setPermissions] = useState({ create: true, view: true, edit: true, delete: true });

  const currentUser = (() => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return null;
      const parsed = JSON.parse(userStr);
      return parsed?.user || parsed;
    } catch (_err) {
      return null;
    }
  })();
  const isSystemCaregiver = currentUser?.role_id === 7;
  const canCreate = !isSystemCaregiver || permissions.create;
  const canView = !isSystemCaregiver || permissions.view;
  const canEdit = !isSystemCaregiver || permissions.edit;
  const canDelete = !isSystemCaregiver || permissions.delete;

  const scopedPracticeId = React.useMemo(() => {
    const pid = currentUser?.practice_id ?? currentUser?.practiceId ?? currentUser?.practices?.[0]?.id;
    return pid != null && pid !== "" ? String(pid) : "";
  }, [currentUser]);

  const defaultPracticeId = React.useMemo(() => {
    if (scopedPracticeId && practices.some((p) => String(p.id) === String(scopedPracticeId))) {
      return String(scopedPracticeId);
    }
    if (practices.length === 1) {
      return String(practices[0].id);
    }
    if (scopedPracticeId) {
      return String(scopedPracticeId);
    }
    return "";
  }, [scopedPracticeId, practices]);

  const isPracticeDisabled = practices.length === 1 || (Boolean(scopedPracticeId) && currentUser?.role_id !== 1 && currentUser?.role_id !== 2);

  useEffect(() => {
    updateBreadcrumbs([
      { label: "Calendar Settings", path: "/calendar-settings/visit-type" },
      { label: "Visit Type", path: null, active: true }
    ]);
    return () => {
      clearBreadcrumbs();
    };
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  // Sorting Handler
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Derived State for Filtering, Sorting, and Pagination
  const sortedItems = React.useMemo(() => {
    let sortableItems = [...visitTypes];

    // First, filter
    sortableItems = sortableItems.filter(item =>
      matchesWordStartAny([item.title, item.description, item.practice_name], searchQuery)
    );

    // Then, sort
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [visitTypes, searchQuery, sortConfig]);

  const effectiveItemsPerPage = itemsPerPage === "all" ? sortedItems.length || 1 : Number(itemsPerPage);
  const indexOfLastItem = currentPage * effectiveItemsPerPage;
  const indexOfFirstItem = indexOfLastItem - effectiveItemsPerPage;
  const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

  const fetchVisitTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await visitTypeAPI.getVisitTypes();
      if (response.success) {
        setVisitTypes(response.data || []);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch visit types");
    } finally {
      setLoading(false);
    }
  }, []);

  // Pagination Change Handler with Scroll to Top
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchPractices = useCallback(async () => {
    try {
      const response = await visitTypeAPI.getPractices();
      if (response.success) {
        const loadedPractices = response.data || [];
        setPractices(loadedPractices);
        const resolvedDefault =
          scopedPracticeId && loadedPractices.some((p) => String(p.id) === String(scopedPracticeId))
            ? String(scopedPracticeId)
            : loadedPractices.length === 1
              ? String(loadedPractices[0].id)
              : scopedPracticeId || "";
        if (resolvedDefault) {
          setFormData((prev) => (prev.practice_id ? prev : { ...prev, practice_id: resolvedDefault }));
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch practices");
    }
  }, [scopedPracticeId]);

  useEffect(() => {
    const loadPermissions = async () => {
      if (!isSystemCaregiver || !currentUser?.id) return;
      try {
        const [createRes, viewRes, editRes, deleteRes] = await Promise.all([
          calendarAPI.checkPermission(currentUser.id, "Create").catch(() => ({ hasPermission: false })),
          calendarAPI.checkPermission(currentUser.id, "View").catch(() => ({ hasPermission: false })),
          calendarAPI.checkPermission(currentUser.id, "Edit").catch(() => ({ hasPermission: false })),
          calendarAPI.checkPermission(currentUser.id, "Delete").catch(() => ({ hasPermission: false }))
        ]);
        setPermissions({
          create: Boolean(createRes?.hasPermission),
          view: Boolean(viewRes?.hasPermission),
          edit: Boolean(editRes?.hasPermission),
          delete: Boolean(deleteRes?.hasPermission)
        });
      } catch (_error) {
        setPermissions({ create: false, view: false, edit: false, delete: false });
      }
    };
    loadPermissions();
  }, [currentUser?.id, isSystemCaregiver]);

  useEffect(() => {
    if (isSystemCaregiver && !canView) {
      toast.error("You do not have permission to view visit types.");
    }
  }, [isSystemCaregiver, canView]);

  useEffect(() => {
    fetchVisitTypes();
    fetchPractices();
  }, [fetchVisitTypes, fetchPractices]);

  const handleAddClick = () => {
    if (!canCreate) {
      toast.error("You do not have permission to create visit types.");
      return;
    }
    setFormData({
      practice_id: defaultPracticeId,
      title: "",
      description: "",
      duration: "",
      status: "active",
      color: "#FF5733"
    });
    setErrors({});
    setShowAddModal(true);
  };

  const handleEditClick = async (visitTypeId) => {
    if (!canEdit) {
      toast.error("You do not have permission to edit visit types.");
      return;
    }
    try {
      const response = await visitTypeAPI.getVisitType(visitTypeId);
      if (response.success) {
        const { visitType, practices: practicesData } = response.data;
        setEditingVisitType(visitType);
        setPractices(practicesData || practices);
        setFormData({
          practice_id: visitType.practice_id || "",
          title: visitType.title || "",
          description: visitType.description || "",
          duration: visitType.duration || "",
          status: visitType.status || "active",
          color: visitType.color || "#FF5733"
        });
        setErrors({});
        setShowEditModal(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch visit type");
    }
  };

  const handleDeleteClick = (visitType) => {
    if (!canDelete) {
      toast.error("You do not have permission to delete visit types.");
      return;
    }
    setVisitTypeToDelete(visitType);
    setShowDeleteModal(true);
  };

  const cancelDeleteVisitType = () => {
    if (isDeletingVisitType) return;
    setShowDeleteModal(false);
    setVisitTypeToDelete(null);
  };

  const confirmDeleteVisitType = async () => {
    if (!visitTypeToDelete?.id) return;

    try {
      setIsDeletingVisitType(true);
      const response = await visitTypeAPI.deleteVisitType(visitTypeToDelete.id);
      if (response.success) {
        toast.success(response.message || "Visit type deleted successfully");
        setVisitTypes((prev) => prev.filter((item) => item.id !== visitTypeToDelete.id));
        setShowDeleteModal(false);
        setVisitTypeToDelete(null);
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete visit type");
    } finally {
      setIsDeletingVisitType(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.practice_id) {
      newErrors.practice_id = "Practice is required";
    }

    if (!formData.title || formData.title.trim() === "") {
      newErrors.title = "Visit type title is required";
    } else if (formData.title.length > 30) {
      newErrors.title = "Title must not exceed 30 characters";
    }

    if (!formData.duration || formData.duration < 1 || formData.duration > 999) {
      newErrors.duration = "Duration must be between 1 and 999 minutes";
    }

    if (!formData.status || !["active", "inactive"].includes(formData.status)) {
      newErrors.status = "Status is required";
    }

    if (!formData.color || !/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = "Color must be a valid hex code (e.g., #FF5733)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingVisitType && !canCreate) {
      toast.error("You do not have permission to create visit types.");
      return;
    }
    if (editingVisitType && !canEdit) {
      toast.error("You do not have permission to edit visit types.");
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      if (editingVisitType) {
        const response = await visitTypeAPI.updateVisitType(editingVisitType.id, formData);
        if (response.success) {
          toast.success(response.message || "Visit type updated successfully");
          setShowEditModal(false);
          const updatedFromApi = response.data?.visitType || response.data?.data || null;
          setVisitTypes((prev) =>
            prev.map((item) =>
              item.id === editingVisitType.id
                ? {
                  ...item,
                  ...(updatedFromApi || formData),
                  id: editingVisitType.id,
                  practice_name:
                    updatedFromApi?.practice_name ||
                    practices.find((p) => String(p.id) === String(formData.practice_id))?.practice_name ||
                    item.practice_name
                }
                : item
            )
          );
        }
      } else {
        const response = await visitTypeAPI.createVisitType(formData);
        if (response.success) {
          toast.success(response.message || "Visit type created successfully");
          setShowAddModal(false);
          const created = response.data?.visitType || response.data?.data || null;
          if (created?.id) {
            setVisitTypes((prev) => [created, ...prev]);
          } else {
            // Fallback: fetch list if backend doesn't return created object.
            fetchVisitTypes();
          }
        }
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        // Handle validation errors from backend
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to save visit type");
      }
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    let formatted = "";

    if (hours > 0) {
      formatted += hours + " hour" + (hours > 1 ? "s" : "");
    }

    if (hours > 0 && remainingMinutes > 0) {
      formatted += " ";
    }

    if (remainingMinutes > 0) {
      formatted += remainingMinutes + " minute" + (remainingMinutes > 1 ? "s" : "");
    }

    return formatted || "0 minutes";
  };

  const columns = [
    {
      key: "rowNo",
      title: "#",
      sortable: false,
      className: "reusable-table-centered-col",
      render: (_row, rowIndex) => indexOfFirstItem + rowIndex + 1,
    },
    { key: "practice_name", title: "Practice Name", sortable: true, render: (row) => row.practice_name || "N/A" },
    { key: "title", title: "Visit Type", sortable: true, render: (row) => row.title || "N/A" },
    { key: "description", title: "Visit Type Description", sortable: true, render: (row) => row.description || "N/A" },
    {
      key: "duration",
      title: "Duration",
      sortable: true,
      render: (row) => row.duration_formatted || formatDuration(row.duration),
    },
    {
      key: "status",
      title: "Status",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (row) => <StatusBadge status={row.status || "N/A"} />,
    },
    {
      key: "color",
      title: "Color",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (row) =>
        row.color ? (
          <div className="visit-type-color-display" style={{ justifyContent: "center" }}>
            <div
              className="visit-type-color-box"
              style={{ backgroundColor: row.color }}
              title={row.color}
            />
          </div>
        ) : (
          "N/A"
        ),
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      key: "action",
      title: "Action",
      sortable: false,
      className: "reusable-table-centered-col",
      render: (row) => (
        <ActionButtons
          onEdit={canEdit ? () => handleEditClick(row.id) : undefined}
          onDelete={canDelete ? () => handleDeleteClick(row) : undefined}
          hideView
          hideEdit={!canEdit}
          hideDelete={!canDelete}
        />
      ),
    });
  }

  return (
    <div className="list-screen-page-container visit-type-page">
      <div className="list-screen-top-bar">
        <div className="list-screen-title-section">
          <h2 className="dashboard-title list-screen-title-text">Visit Type List</h2>
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchQuery}
            onChange={(val) => {
              setSearchQuery(val);
              setCurrentPage(1);
            }}
            inputId="visit-type-search"
          />
          {canCreate && (
            <button type="button" className="list-screen-add-btn" onClick={handleAddClick}>
              <FontAwesomeIcon icon={faPlus} className="list-screen-add-icon" />
              Add Visit Type
            </button>
          )}
        </div>
      </div>

      <div className="visit-type-table-container">
          {!canView ? (
            <div style={{ padding: "24px", textAlign: "center" }}>
              You do not have permission to view visit types.
            </div>
          ) : (
          <Table
            columns={columns}
            data={currentItems}
            sortBy={sortConfig.key}
            sortOrder={sortConfig.direction === "ascending" ? "asc" : "desc"}
            onSort={requestSort}
            isLoading={loading}
            loadingMode="skeleton"
            skeletonRows={Math.min(itemsPerPage, 10)}
            emptyMessage="No visit types found"
          />
          )}

          {canView && (
            <ListScreenPaginationBar
              entriesValue={itemsPerPage === "all" ? "all" : String(itemsPerPage)}
              onEntriesChange={(value) => {
                setItemsPerPage(value === "all" ? "all" : Number(value));
                handlePageChange(1);
              }}
              entryOptions={[
                { value: "10", label: "10" },
                { value: "30", label: "30" },
                { value: "50", label: "50" },
                { value: "all", label: "All" }
              ]}
              showPageSelector
              currentPage={currentPage}
              totalPages={itemsPerPage === "all" ? 1 : Math.ceil(sortedItems.length / effectiveItemsPerPage) || 1}
              onPageChange={handlePageChange}
            />
          )}
        </div>

      {/* Add Modal */}
      {
        showAddModal && (
          <div className="modal-overlay">
            <div className="visit-type-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="visit-type-modal-header">
                <h3>Add Visit Type</h3>
                <button className="visit-type-close-btn" onClick={() => setShowAddModal(false)}>
                  <i className="fa fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="visit-type-modal-body">
                  <div className="visit-type-modal-body-rows">
                    {/* Row 1: Practice & Visit Type */}
                    <div className="visit-type-modal-row">
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Practice <span className="visit-type-required">*</span>
                          </label>
                          <select
                            value={formData.practice_id}
                            onChange={(e) =>
                              setFormData({ ...formData, practice_id: e.target.value })
                            }
                            className={`${errors.practice_id ? "error" : ""}${isPracticeDisabled ? " visit-type-no-arrow" : ""}`.trim()}
                            disabled={isPracticeDisabled}
                          >
                            {(!formData.practice_id || (practices.length > 1 && (!scopedPracticeId || currentUser?.role_id === 1 || currentUser?.role_id === 2))) && (
                              <option value="">--Select--</option>
                            )}
                            {practices.map((practice) => (
                              <option key={practice.id} value={String(practice.id)}>
                                {practice.practice_name}
                              </option>
                            ))}
                          </select>
                          {errors.practice_id && (
                            <span className="visit-type-error-message">{errors.practice_id}</span>
                          )}
                        </div>
                      </div>
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Visit Type <span className="visit-type-required">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, "").substring(0, 30);
                              setFormData({ ...formData, title: value });
                            }}
                            className={errors.title ? "error" : ""}
                            maxLength={30}
                          />
                          {errors.title && (
                            <span className="visit-type-error-message">{errors.title}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Duration & Status */}
                    <div className="visit-type-modal-row">
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Duration (Minutes) <span className="visit-type-required">*</span>
                          </label>
                          <input
                            type="number"
                            value={formData.duration}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 3) {
                                setFormData({ ...formData, duration: value });
                              }
                            }}
                            className={errors.duration ? "error" : ""}
                            min="1"
                            max="999"
                          />
                          {errors.duration && (
                            <span className="visit-type-error-message">{errors.duration}</span>
                          )}
                        </div>
                      </div>
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Status <span className="visit-type-required">*</span>
                          </label>
                          <select
                            value={formData.status}
                            onChange={(e) =>
                              setFormData({ ...formData, status: e.target.value })
                            }
                            className={errors.status ? "error" : ""}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          {errors.status && (
                            <span className="visit-type-error-message">{errors.status}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Color & Description */}
                    <div className="visit-type-modal-row">
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Color <span className="visit-type-required">*</span>
                          </label>
                          <div className="visit-type-color-input-group">
                            <input
                              type="text"
                              value={formData.color}
                              onChange={(e) =>
                                setFormData({ ...formData, color: e.target.value })
                              }
                              placeholder="#FF5733"
                              className={errors.color ? "error" : ""}
                              maxLength={7}
                            />
                            <input
                              type="color"
                              value={formData.color}
                              onChange={(e) =>
                                setFormData({ ...formData, color: e.target.value })
                              }
                              className="visit-type-color-picker"
                            />
                          </div>
                          {errors.color && (
                            <span className="visit-type-error-message">{errors.color}</span>
                          )}
                        </div>
                      </div>
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>Visit Type Description</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) =>
                              setFormData({ ...formData, description: e.target.value })
                            }
                            rows="3"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="visit-type-modal-footer">
                  <button type="button" className="visit-type-cancel-btn" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="visit-type-submit-btn">
                    Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Edit Modal */}
      {
        showEditModal && (
          <div className="modal-overlay">
            <div className="visit-type-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="visit-type-modal-header">
                <h3>Edit Visit Type</h3>
                <button className="visit-type-close-btn" onClick={() => setShowEditModal(false)}>
                  <i className="fa fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="visit-type-modal-body">
                  <div className="visit-type-modal-body-rows">
                    {/* Row 1: Practice & Visit Type */}
                    <div className="visit-type-modal-row">
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Practice <span className="visit-type-required">*</span>
                          </label>
                          <select
                            value={formData.practice_id}
                            onChange={(e) =>
                              setFormData({ ...formData, practice_id: e.target.value })
                            }
                            className={`${errors.practice_id ? "error" : ""}${isPracticeDisabled ? " visit-type-no-arrow" : ""}`.trim()}
                            disabled={isPracticeDisabled}
                          >
                            {(!formData.practice_id || (practices.length > 1 && (!scopedPracticeId || currentUser?.role_id === 1 || currentUser?.role_id === 2))) && (
                              <option value="">--Select--</option>
                            )}
                            {practices.map((practice) => (
                              <option key={practice.id} value={String(practice.id)}>
                                {practice.practice_name}
                              </option>
                            ))}
                          </select>
                          {errors.practice_id && (
                            <span className="visit-type-error-message">{errors.practice_id}</span>
                          )}
                        </div>
                      </div>
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Visit Type <span className="visit-type-required">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, "").substring(0, 30);
                              setFormData({ ...formData, title: value });
                            }}
                            className={errors.title ? "error" : ""}
                            maxLength={30}
                          />
                          {errors.title && (
                            <span className="visit-type-error-message">{errors.title}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Duration & Status */}
                    <div className="visit-type-modal-row">
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Duration (Minutes) <span className="visit-type-required">*</span>
                          </label>
                          <input
                            type="number"
                            value={formData.duration}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 3) {
                                setFormData({ ...formData, duration: value });
                              }
                            }}
                            className={errors.duration ? "error" : ""}
                            min="1"
                            max="999"
                          />
                          {errors.duration && (
                            <span className="visit-type-error-message">{errors.duration}</span>
                          )}
                        </div>
                      </div>
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Status <span className="visit-type-required">*</span>
                          </label>
                          <select
                            value={formData.status}
                            onChange={(e) =>
                              setFormData({ ...formData, status: e.target.value })
                            }
                            className={errors.status ? "error" : ""}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          {errors.status && (
                            <span className="visit-type-error-message">{errors.status}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Color & Description */}
                    <div className="visit-type-modal-row">
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>
                            Color <span className="visit-type-required">*</span>
                          </label>
                          <div className="visit-type-color-input-group">
                            <input
                              type="text"
                              value={formData.color}
                              onChange={(e) =>
                                setFormData({ ...formData, color: e.target.value })
                              }
                              placeholder="#FF5733"
                              className={errors.color ? "error" : ""}
                              maxLength={7}
                            />
                            <input
                              type="color"
                              value={formData.color}
                              onChange={(e) =>
                                setFormData({ ...formData, color: e.target.value })
                              }
                              className="visit-type-color-picker"
                            />
                          </div>
                          {errors.color && (
                            <span className="visit-type-error-message">{errors.color}</span>
                          )}
                        </div>
                      </div>
                      <div className="visit-type-modal-col">
                        <div className="visit-type-form-group">
                          <label>Visit Type Description</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) =>
                              setFormData({ ...formData, description: e.target.value })
                            }
                            rows="3"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="visit-type-modal-footer">
                  <button type="button" className="visit-type-cancel-btn" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="visit-type-submit-btn">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={cancelDeleteVisitType}
        onConfirm={confirmDeleteVisitType}
        isDeleting={isDeletingVisitType}
        message={
          <p>
            Are you sure you want to delete the visit type{" "}
            <strong>{visitTypeToDelete?.title || "this visit type"}</strong>?
          </p>
        }
      />
    </div >
  );
};

export default VisitType;
